import { useEffect, useMemo, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Option, OptionGroup, useNotifications } from '@joor/design-system'
import { useSelector } from 'react-redux'

import {
  AccountUserType,
  ReassignConnectionsDocument,
  ReassignConnectionsInput,
  UserManagementAccountUsersDocument,
} from '__generated__/atlas-types'

import { formatNameWithCodeAndNameInBrackets } from 'utils/formatters'
import { logMutationError } from 'utils/sdks/datadog'
import {
  relayConnectionToArray,
  toGlobalId,
} from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import usePortal from 'hooks/usePortal'
import { getAccountId } from 'store/currentUser/selectors'

import messages from './reassignConnectionsModal.messages'
import {
  AccountUser,
  Actions,
  Statuses,
} from './reassignConnectionsModal.types'
import useStore from './store/useReassignConnectionsStore'

const CONSOLE_LOG_KEY = '[ReassignConnectionsModal]'

type ExtendedOption = Option & { numberConnections?: number }

export const useGetSalesReps = (): {
  loading: boolean
  salesReps: AccountUser[]
} => {
  const { data, loading } = useQuery(UserManagementAccountUsersDocument, {
    client: atlasClient,
  })

  const salesReps =
    data && data?.activeViewingAccount?.accountUsers
      ? relayConnectionToArray(data.activeViewingAccount.accountUsers)
      : []

  return { loading, salesReps }
}

const getSalesRepLabel = (accountUser: AccountUser) => {
  if (accountUser.displayName) {
    return formatNameWithCodeAndNameInBrackets(
      accountUser.displayName,
      accountUser.displayCode,
    )
  }

  return accountUser.user.email || ''
}

export const useReassignConnectionsModal = (onClose: () => void) => {
  const Portal = usePortal()

  const action = useStore((state) => state.action)
  const sourceUser = useStore((state) => state.sourceUser)
  const targetUser = useStore((state) => state.targetUser)
  const setSalesReps = useStore((state) => state.setSalesReps)
  const resetStore = useStore((state) => state.resetStore)

  const [isMappingUsers, setIsMappingUsers] = useState(true)
  const [isReassigning, setIsReassigning] = useState(false)

  const { reassignConnections } = useReassignConnections()
  const { loading: areSalesRepsLoading, salesReps } = useGetSalesReps()

  useEffect(() => {
    if (!areSalesRepsLoading) {
      setSalesReps({ salesReps })
    }
  }, [areSalesRepsLoading, salesReps, setSalesReps])

  const {
    optionGroups,
    activeSalesRepOptions,
  }: {
    optionGroups: OptionGroup[]
    activeSalesRepOptions: Option[]
  } = useMemo(() => {
    const groups = [
      {
        label: Statuses.ACTIVE,
        options: [] as ExtendedOption[],
      },
      {
        label: Statuses.DISABLED,
        options: [] as ExtendedOption[],
      },
    ]
    const activeSalesRepOptions: Option[] = []

    if (areSalesRepsLoading || !salesReps.length) {
      return { optionGroups: groups, activeSalesRepOptions }
    }

    salesReps.forEach((accountUser: AccountUser) => {
      if ((accountUser?.numberConnections || 0) > 0) {
        const optionGroupIndex = accountUser.active ? 0 : 1
        groups[optionGroupIndex].options.push({
          label: getSalesRepLabel(accountUser),
          value: accountUser.id,
          numberConnections: accountUser.numberConnections || 0,
        })
      }
      if (
        accountUser.active &&
        accountUser.displayName &&
        accountUser.userType !== AccountUserType.INTERNAL
      ) {
        activeSalesRepOptions.push({
          label: formatNameWithCodeAndNameInBrackets(
            accountUser.displayName,
            accountUser.displayCode,
          ),
          value: accountUser.id,
        })
      }
    })
    setIsMappingUsers(false)
    return { optionGroups: groups, activeSalesRepOptions }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areSalesRepsLoading])

  const isLoading = areSalesRepsLoading || isMappingUsers || isReassigning

  const isConfirmDisabled =
    isLoading || !sourceUser || (action === Actions.REASSIGN && !targetUser)

  const handleOnClose = () => {
    resetStore()
    onClose()
  }

  const handleConfirm = async () => {
    setIsReassigning(true)
    const { error } = await reassignConnections()
    setIsReassigning(false)
    if (!error) {
      handleOnClose()
    }
  }

  return {
    isConfirmDisabled,
    isLoading,
    isReassigning,
    optionGroups,
    sourceUser,
    handleConfirm,
    handleOnClose,
    Portal,
    activeSalesRepOptions,
    action,
  }
}

export const useReassignConnections = () => {
  const accountId = useSelector((state) => getAccountId(state))
  const action = useStore((state) => state.action)
  const sourceUser = useStore((state) => state.sourceUser)
  const targetUser = useStore((state) => state.targetUser)
  const { addToasts } = useNotifications()
  const [mutate] = useMutation(ReassignConnectionsDocument, {
    client: atlasClient,
    refetchQueries: [UserManagementAccountUsersDocument],
  })

  const reassignConnections = async () => {
    if (!sourceUser || (action === Actions.REASSIGN && !targetUser)) {
      return { error: true }
    }

    const variables: ReassignConnectionsInput = {
      accountId: toGlobalId('Account', accountId) ?? '',
      sourceAccountUserId: sourceUser.id,
      targetAccountUserId: action === Actions.REASSIGN ? targetUser?.id : null,
    }

    try {
      const response = await mutate({ variables })

      if (
        response.errors ||
        !response.data ||
        !response.data.reassignConnections?.success
      ) {
        throw response?.errors?.length
          ? response.errors[0]
          : new Error('Mutation Error')
      }

      const title =
        action === Actions.REASSIGN
          ? messages.reassignSuccessTitle
          : messages.removeSuccessTitle

      const description =
        action === Actions.REASSIGN
          ? messages.reassignSuccessDesc
              .replace(
                '{amount}',
                sourceUser.numberConnections?.toString() || '',
              )
              .replace(
                '{old}',
                sourceUser.displayName || sourceUser?.user?.email || '',
              )
              .replace('{new}', targetUser?.displayName || '')
          : messages.removeSuccessDesc
              .replace(
                '{old}',
                sourceUser.displayName || sourceUser?.user?.email || '',
              )
              .replace(
                '{amount}',
                sourceUser.numberConnections?.toString() || '',
              )

      addToasts([
        {
          title,
          description,
          type: 'success',
          shouldCloseManually: true,
        },
      ])

      return { error: false }
    } catch (error) {
      addToasts([
        {
          title: messages.errorTitle,
          description: messages.errorDesc,
          type: 'error',
          shouldCloseManually: true,
        },
      ])

      logMutationError(
        'ReassignConnectionsMutation',
        variables,
        `${CONSOLE_LOG_KEY}[Mutation] Error: ${(error as Error).message}`,
      )
      return { error: true }
    }
  }

  return { reassignConnections }
}
